import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useOnboarding } from '../../context/OnboardingContext';
import Sidebar from './Sidebar';
import api from '../../services/api';

const DashboardLayout = () => {
  const { isAuthenticated, loading, user } = useAuth();
  const { isDark } = useTheme();
  const { isOnboardingRequired } = useOnboarding();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  
  
  const endImpersonation = async () => {
    // Restore original token
    const originalToken = localStorage.getItem('originalToken');
    if (originalToken) {
      localStorage.setItem('accessToken', originalToken);
      localStorage.removeItem('originalToken');
      localStorage.removeItem('impersonationToken');
      
      // Fetch the original admin user data
      try {
        const response = await api.get('/auth/me');
        localStorage.setItem('user', JSON.stringify(response.data.user));
      } catch (error) {
        console.error('Failed to fetch admin user data:', error);
      }
      
      window.location.href = '/admin';
    }
  };

  useEffect(() => {
    // Redirect to login if not authenticated after loading
    if (!loading && !isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [loading, isAuthenticated, navigate]);

  // Show loading state while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className={`text-xl ${isDark ? 'text-white' : 'text-gray-800'}`}>
            Loading...
          </div>
        </div>
      </div>
    );
  }

  // Don't render anything if not authenticated
  if (!isAuthenticated || !user) {
    return null;
  }

  // If onboarding is required, render a minimal layout 
  // The onboarding modal will overlay on top
  if (isOnboardingRequired) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="glass-spinner w-12 h-12 mx-auto mb-4"></div>
          <p className={`text-mono text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Please complete onboarding to continue...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen" style={{ minHeight: '100vh' }}>
      {/* Impersonation Indicator */}
      {user?.isImpersonation && (
        <div className="fixed top-0 left-0 right-0 z-50 glass-alert-warning p-3 text-center">
          <div className="flex items-center justify-center space-x-3">
            <span className="text-mono text-sm font-medium text-white">
              Viewing as: {user.email} (by {user.impersonatedByEmail})
            </span>
            <button
              onClick={endImpersonation}
              className="btn-glass text-sm px-3 py-1 font-medium hover:bg-yellow-600/20"
            >
              End Impersonation
            </button>
          </div>
        </div>
      )}
      
      {/* Sidebar */}
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      {/* Main Content Area */}
      <div className={`flex-1 lg:ml-72 transition-margin duration-300 ${user?.isImpersonation ? 'mt-12' : ''}`}>
        {/* Mobile Header with Menu Button */}
        <div className="lg:hidden fixed top-0 right-0 left-0 z-30 glass-card border-b border-white/10 px-4 py-3">
          <button
            onClick={() => setSidebarOpen(true)}
            className="btn-glass p-2 rounded-lg hover:scale-105 transform transition-all"
          >
            <svg className={`w-6 h-6 ${isDark ? 'text-white' : 'text-gray-800'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
        
        {/* Page Content */}
        <main className={`min-h-screen ${isDark ? 'bg-black/20' : 'bg-white/20'} backdrop-blur-sm`}>
          <div className="p-6 lg:p-8 pt-20 lg:pt-8 max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;